if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;

      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;

      abortController = undefined;
      pendingRequestUrl = null;

      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        // più safe: evita crash se per qualche motivo non è mai stata settata
        try {
          this.onVariantChangeUnsubscriber?.();
        } catch (e) {
          console.warn('[product-info] onVariantChangeUnsubscriber cleanup failed', e);
        }

        try {
          this.cartUpdateUnsubscriber?.();
        } catch (e) {
          console.warn('[product-info] cartUpdateUnsubscriber cleanup failed', e);
        }

        // se il componente muore durante una fetch, abort
        this.abortController?.abort();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundaries();

        // Dawn: solo main section si risottoscrive agli update carrello
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((el) => el.classList.add('scroll-trigger--cancel'));
        });

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        // ColorX: in Featured Product (collection) gestiamo localmente per evitare reset Dawn AJAX
        // ("funziona per un istante poi si resetta")
        if (this.dataset.colorx === 'featured') return;

        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target?.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            const nextTitle = html.querySelector('head title')?.innerHTML;
            if (nextTitle) document.querySelector('head title').innerHTML = nextTitle;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      async renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        try {
          const response = await fetch(requestUrl, { signal: this.abortController.signal });

          // più robusto: se Shopify risponde non-OK, eviti parsing HTML inutile
          if (!response.ok) {
            throw new Error(`[product-info] Request failed: ${response.status} ${response.statusText}`);
          }

          const responseText = await response.text();
          this.pendingRequestUrl = null;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);

          // set focus to last clicked option value (se esiste)
          if (targetId) document.querySelector(`#${CSS.escape(targetId)}`)?.focus();
        } catch (error) {
          if (error?.name === 'AbortError') {
            // normale: cambio rapido varianti
            return;
          }
          console.error(error);
        }
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;
        const selectedVariantJson =
          productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;

        try {
          return selectedVariantJson ? JSON.parse(selectedVariantJson) : null;
        } catch (e) {
          console.warn('[product-info] Failed to parse selected variant JSON', e);
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        if (!shouldFetchFullPage) params.push(`section_id=${encodeURIComponent(this.sectionId)}`);

        if (optionValues?.length) {
          params.push(`option_values=${encodeURIComponent(optionValues.join(','))}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (!variantSelects) return;
        HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);

          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (!source || !destination) return;

            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;

          // Se non c'è variante valida, svuota il value MA NON triggerare change
          if (!variantId) {
            input.value = '';
            return;
          }

          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const next = this.buildUrlWithVariant(url, variantId);

        // share button sempre aggiornato
        this.querySelector('share-button')?.updateUrl(next);

        if (this.dataset.updateUrl === 'false') return;

        // evita crash in contesti strani (iframe/blocked history)
        try {
          window.history.replaceState({}, '', next.replace(window.shopUrl, ''));
        } catch (e) {
          console.warn('[product-info] history.replaceState failed', e);
        }
      }

      buildUrlWithVariant(url, variantId) {
        const base = window.shopUrl || window.location.origin;

        try {
          const u = new URL(url, base);
          if (variantId) {
            u.searchParams.set('variant', String(variantId));
          } else {
            u.searchParams.delete('variant');
          }
          return u.toString();
        } catch (e) {
          // fallback super semplice
          return `${base}${url}${variantId ? `?variant=${variantId}` : ''}`;
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');

        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector('media-gallery ul');

        if (!mediaGallerySource || !mediaGalleryDestination) return;

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

          const sourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(sourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(sourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));

          return [sourceItems, sourceSet, sourceMap];
        };

        let [sourceItems, sourceSet, sourceMap] = refreshSourceData();

        const destinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
        const destinationSet = new Set(destinationItems.map((el) => el.dataset.mediaId));

        let shouldRefresh = false;

        // add items from new data not present in DOM
        for (let i = destinationItems.length - 1; i >= 0; i--) {
          const id = destinationItems[i].dataset.mediaId;
          if (!sourceSet.has(id)) {
            mediaGallerySource.prepend(destinationItems[i]);
            shouldRefresh = true;
          }
        }

        // remove items from DOM not present in new data
        for (let i = 0; i < sourceItems.length; i++) {
          const id = sourceItems[i].dataset.mediaId;
          if (!destinationSet.has(id)) {
            sourceItems[i].remove();
            shouldRefresh = true;
          }
        }

        // refresh
        if (shouldRefresh) [sourceItems, sourceSet, sourceMap] = refreshSourceData();

        // if media galleries don't match, sort to match new data order
        destinationItems.forEach((destinationItem, destinationIndex) => {
          const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
          if (!sourceData || sourceData.index === destinationIndex) return;

          mediaGallerySource.insertBefore(
            sourceData.item,
            mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
          );

          [sourceItems, sourceSet, sourceMap] = refreshSourceData();
        });

        // set featured media as active in the media gallery
        this.querySelector('media-gallery')?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

        // update media modal
        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        const newModalContent = html.querySelector('product-modal .product-media-modal__content');
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundaries() {
        if (!this.quantityInput) return;

        const cartQuantity = this.quantityInput.dataset.cartQuantity
          ? parseInt(this.quantityInput.dataset.cartQuantity, 10)
          : 0;

        const minRaw = this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min, 10) : 1;
        const maxRaw = this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max, 10) : null;
        const step = this.quantityInput.step ? parseInt(this.quantityInput.step, 10) : 1;

        let min = minRaw;
        const max = maxRaw === null ? null : maxRaw - cartQuantity;

        if (max !== null) min = Math.min(min, max);
        if (cartQuantity >= minRaw) min = Math.min(min, step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => spinner?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        this.setQuantityBoundaries();

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (const attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
              else current.removeAttribute(attribute);
            }
          } else {
            current.innerHTML = updated.innerHTML;

            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);

                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector('product-form');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
